import { createSelector } from '@reduxjs/toolkit';
import _, { isEmpty, keyBy } from 'lodash';
import type { RootState } from 'src/root/store';

import { AdvancementStatus } from '@appTypes/enums';
import { mbcApi } from '@modules/meritBadgeCounselors/api';
import { currentUserMIDSel } from '@user/duck/selectorsTyped';

import type { ModalName } from '../types';
import { ToApproveRowKeys, YouthByMBRowKey } from './utils';

const moduleSel = (state: RootState) => state.mbcDashboard;

export const searchSel = createSelector(moduleSel, state => state.youthSearch);
export const mbcProfileSel = createSelector(
  [currentUserMIDSel, state => state],
  (memberId, state) => {
    const result = mbcApi.endpoints.GetMeritBadgeCounselorProfile.select({
      memberId,
    })(state);

    return result.data;
  },
);

export const mbcProfileMIDSel = createSelector(
  mbcProfileSel,
  profile => profile?.memberId,
);
export const mbcProfileGuidSel = createSelector(
  mbcProfileSel,
  profile => profile?.personGuid,
);
export const mbcProfileUserIdSel = createSelector(
  mbcProfileSel,
  profile => profile?.userId,
);

export const dashboardYouthsSel = createSelector(mbcProfileSel, mbcProfile => {
  if (!mbcProfile) return [];
  return mbcProfile.youths || [];
});

export const mbcProfileCounselingMBsSel = createSelector(
  mbcProfileSel,
  mbcProfile => {
    if (!mbcProfile) return [];
    return mbcProfile.meritBadges || [];
  },
);

export const dashboardYouthsByMIDSel = createSelector(
  dashboardYouthsSel,
  youths => keyBy(youths, 'memberId'),
);

export const modalSel = (modal: ModalName) =>
  createSelector(moduleSel, state => state.modals[modal]);

export const mbcYouthsFilteredSel = createSelector(
  [dashboardYouthsSel, searchSel],
  (youths, searchTerm) => {
    if (!youths) return [];

    const res = youths.filter(item => {
      const searchProps = [
        item.firstName,
        item.middleName,
        item.lastName,
        item.memberId,
      ];
      return searchProps
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });

    return res;
  },
);

export const dashboardViewBySel = createSelector(
  moduleSel,
  state => state.viewBy,
);

export const dashboardSortByNameSel = createSelector(
  moduleSel,
  state => state.sortByName,
);

export const dashboardFilterByStatusSel = createSelector(
  moduleSel,
  state => state.filterByStatus,
);

export const selectedYouthSel = createSelector(
  moduleSel,
  state => state.selectedYouth,
);

export const navigatedToMbSel = createSelector(
  moduleSel,
  state => state.navigatedToMb,
);

export const selectedYouthByMBSel = createSelector(
  moduleSel,
  state => state.selectedYouthByMB,
);

export const mbcYouthsByMBSel = createSelector(
  [dashboardYouthsSel, searchSel, dashboardFilterByStatusSel],
  (youths, searchTerm, filterByStatus) => {
    const data = _.chain(youths)
      .filter(item => {
        const searchProps = [
          item.firstName,
          item.middleName,
          item.lastName,
          item.memberId,
        ];
        return searchProps
          .join(' ')
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      })
      .flatMap(youth =>
        youth.meritBadgesCounseling
          .filter(mb => {
            if (filterByStatus) {
              return mb.status?.replace(/\s+/g, '') === filterByStatus;
            }
            return true;
          })
          .map(mb => ({
            mb,
            youth,
            rowKey: YouthByMBRowKey.getKey({
              memberId: youth.memberId,
              meritBadgeId: mb.meritBadgeId,
            }),
          })),
      )
      .groupBy(item => item.mb.meritBadgeId)
      .value();
    return data;
  },
);

const mbcDashboardQuerySel = createSelector(
  [currentUserMIDSel, state => state],
  (memberId, state) => {
    const result = mbcApi.endpoints.getMBCDashboard.select({
      personGuidOrMID: memberId,
    })(state);
    return result.data;
  },
);

export const toApproveMeritBadgesSel = createSelector(
  mbcDashboardQuerySel,
  dashboard => {
    const meritBadges = _.chain(dashboard)
      .defaultTo([])
      .filter(item => item.meritBadges.length > 0)
      .flatMap(youth =>
        youth.meritBadges.map(mb => ({
          /** Key for table as unique id */
          rowKey: `${mb.meritBadgeId}:${youth.memberId[0]}`,
          mb,
          youth,
        })),
      )
      .filter(item => item.mb.status === AdvancementStatus.Completed)
      .value();
    return meritBadges;
  },
);

export const filteredToApproveMBsSel = createSelector(
  toApproveMeritBadgesSel,
  searchSel,
  (mbs, searchTerm) => {
    if (!searchTerm) return mbs;

    return mbs.filter(mb => {
      const searchProps = [
        mb.youth.firstName,
        mb.youth.lastName,
        mb.youth.middleName,
        mb.mb.name,
      ];
      return searchProps
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
  },
);

export const toApproveRequirementsSel = createSelector(
  mbcDashboardQuerySel,
  dashboard => {
    const requirements = _.chain(dashboard)
      .defaultTo([])
      .flatMap(item =>
        item.meritBadges.map(mb => ({
          mb,
          youth: item,
          requirements: mb.requirements,
        })),
      )
      .filter(item => item.requirements.length > 0)
      .flatMap(item =>
        item.requirements.map(req => ({
          requirement: req,
          rowKey: ToApproveRowKeys.getRequirementKey({
            memberId: item.youth.memberId[0],
            meritBadgeId: item.mb.meritBadgeId,
            requirementId: req.id,
          }),
          mb: item.mb,
          youth: item.youth,
        })),
      )
      .value();
    return requirements;
  },
);
export const filteredToApproveReqsSel = createSelector(
  toApproveRequirementsSel,
  searchSel,
  (reqs, searchTerm) => {
    if (!searchTerm) return reqs;

    return reqs.filter(req => {
      const searchProps = [
        req.youth.firstName,
        req.youth.lastName,
        req.youth.middleName,
        req.mb.name,
        req.requirement.listNumber,
        req.requirement.number,
      ];
      return searchProps
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    });
  },
);

const selectedMBsSel = createSelector(
  moduleSel,
  state => state.selectedMeritBadges,
);
const selectedReqsSel = createSelector(
  moduleSel,
  state => state.selectedRequirements,
);

export const selectedMBsToApproveSel = createSelector(
  [toApproveMeritBadgesSel, selectedMBsSel],
  (dataSource, selection) => {
    const keySet = new Set(selection);
    const mbs = dataSource.filter(item => keySet.has(item.rowKey));
    return mbs;
  },
);

export const selectedReqsToApproveSel = createSelector(
  [toApproveRequirementsSel, selectedReqsSel],
  (dataSource, selection) => {
    const keySet = new Set(selection);
    const mbs = dataSource.filter(item => keySet.has(item.rowKey));
    return mbs;
  },
);

export const approvalSelectionCountSel = createSelector(
  selectedReqsToApproveSel,
  selectedMBsToApproveSel,
  (reqs, mbs) => reqs.length + mbs.length,
);

export const emptyApprovalSelectionSel = createSelector(
  selectedReqsToApproveSel,
  selectedMBsToApproveSel,
  (reqs, mbs) => isEmpty(reqs) && isEmpty(mbs),
);

export const toApproveCountSel = createSelector(
  toApproveMeritBadgesSel,
  toApproveRequirementsSel,
  (toApproveItems, requirements) => toApproveItems.length + requirements.length,
);
